import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  AlertTriangle,
  Loader2,
  RefreshCw,
  ArrowDownRight,
  Search,
  Download,
  Scale,
  TrendingDown,
  Unlink,
  Filter,
} from 'lucide-react';
import { toast } from 'sonner';

interface ComparisonIssue {
  type: string;
  severity: string;
  accountId: string;
  username: string;
  brand: string;
  partner: string;
  offer: string;
  geo: string;
  currency: string;
  details: string;
  invoiceRevenue?: number;
  reconciliationEarnings?: number;
  partnerCommissionOurShare?: number;
  adwstatsCommission?: number;
  adwstatsCommissionOriginal?: number;
  adwstatsAccountId?: string;
  adwstatsAccountName?: string;
  adwstatsMatchMethod?: string;
  adwstatsDataSynced?: boolean;
  adwstatsCurrency?: string;
  manualCheckAmount?: number;
  manualCheckComment?: string;
  manualCheckSource?: string;
  revenueDifference?: number;
  percentageDifference?: number;
  missingInReconciliation?: boolean;
}

const getAuthToken = (): string | null => {
  try {
    const auth = localStorage.getItem('adwstats-auth');
    if (auth) {
      const parsed = JSON.parse(auth);
      return parsed.state?.token || null;
    }
  } catch { /* ignore */ }
  return null;
};

const fmtCurrency = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v);

const fmtOriginalCurrency = (v: number, currency: string) => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${currency} ${v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
};

type ActiveTab = 'underpaid' | 'unmatched';

const RaketechUnderpaid: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [allIssues, setAllIssues] = useState<ComparisonIssue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<'difference' | 'percentage' | 'partner' | 'invoiced'>('difference');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [activeTab, setActiveTab] = useState<ActiveTab>('underpaid');

  const fetchData = useCallback(async () => {
    const token = getAuthToken();
    if (!token) { toast.error('Not authenticated'); return; }

    setIsLoading(true);
    setError(null);
    try {
      const resp = await fetch('/api/raketech/compare', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await resp.json() as any;
      if (!json.success) throw new Error(json.error || 'Failed');
      if (json.data.empty) {
        setError(json.data.message);
        setAllIssues([]);
      } else {
        setAllIssues(json.data.issues as ComparisonIssue[]);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const underpaidIssues = useMemo(() =>
    allIssues.filter(i => i.type === 'underpayment'),
  [allIssues]);

  const unmatchedIssues = useMemo(() =>
    allIssues.filter(i => i.accountId && !i.adwstatsAccountId && !i.missingInReconciliation),
  [allIssues]);

  const issues = activeTab === 'underpaid' ? underpaidIssues : unmatchedIssues;

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'partner' ? 'asc' : 'asc');
    }
  };

  const filtered = useMemo(() => {
    let items = issues;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      items = items.filter(i =>
        i.accountId?.toLowerCase().includes(q) ||
        i.username?.toLowerCase().includes(q) ||
        i.partner?.toLowerCase().includes(q) ||
        i.brand?.toLowerCase().includes(q) ||
        i.geo?.toLowerCase().includes(q) ||
        i.adwstatsAccountName?.toLowerCase().includes(q)
      );
    }

    return [...items].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'difference') {
        cmp = (a.revenueDifference || 0) - (b.revenueDifference || 0);
      } else if (sortField === 'percentage') {
        cmp = (a.percentageDifference || 0) - (b.percentageDifference || 0);
      } else if (sortField === 'invoiced') {
        cmp = (a.invoiceRevenue || 0) - (b.invoiceRevenue || 0);
      } else {
        cmp = (a.partner || '').localeCompare(b.partner || '');
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [issues, searchTerm, sortField, sortDir]);

  // Underpaid stats
  const totalUnderpaid = useMemo(() =>
    underpaidIssues.reduce((s, i) => s + Math.abs(i.revenueDifference || 0), 0),
  [underpaidIssues]);

  const totalInvoiced = useMemo(() =>
    underpaidIssues.reduce((s, i) => s + (i.invoiceRevenue || 0), 0),
  [underpaidIssues]);

  const totalShouldBe = useMemo(() =>
    underpaidIssues.reduce((s, i) => {
      const recon = i.reconciliationEarnings || 0;
      const synced = (i.adwstatsDataSynced !== false && i.adwstatsCommission) ? i.adwstatsCommission : 0;
      const manual = i.manualCheckAmount || 0;
      return s + Math.max(recon, synced, manual);
    }, 0),
  [underpaidIssues]);

  const totalSynced = useMemo(() =>
    underpaidIssues.filter(i => i.adwstatsCommission !== undefined).reduce((s, i) => s + (i.adwstatsCommission || 0), 0),
  [underpaidIssues]);

  const syncedAccountCount = useMemo(() =>
    underpaidIssues.filter(i => i.adwstatsCommission !== undefined && i.adwstatsCommission > 0).length,
  [underpaidIssues]);

  // Unmatched stats
  const unmatchedInvoiceTotal = useMemo(() =>
    unmatchedIssues.reduce((s, i) => s + (i.invoiceRevenue || 0), 0),
  [unmatchedIssues]);

  const unmatchedReconTotal = useMemo(() =>
    unmatchedIssues.reduce((s, i) => s + (i.reconciliationEarnings || 0), 0),
  [unmatchedIssues]);

  const SortHeader: React.FC<{ field: typeof sortField; label: string; align?: string }> = ({ field, label, align }) => (
    <th
      className={`px-3 py-2.5 text-zinc-500 font-medium cursor-pointer hover:text-zinc-300 transition-colors ${align || 'text-left'}`}
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortField === field && <span className="text-emerald-400">{sortDir === 'asc' ? '↑' : '↓'}</span>}
      </span>
    </th>
  );

  const hasData = underpaidIssues.length > 0 || unmatchedIssues.length > 0;

  return (
    <div className="min-h-screen bg-[hsl(225,15%,6%)] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight flex items-center gap-3">
              <AlertTriangle className="h-7 w-7 text-red-400" />
              Commission Errors
            </h1>
            <p className="text-zinc-400 mt-1 text-sm">
              Underpaid accounts and unmatched Raketech accounts we can't verify
            </p>
          </div>
          <button
            onClick={fetchData}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors disabled:opacity-50 shrink-0"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {isLoading && !hasData && (
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3 text-zinc-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading comparison data...</span>
          </div>
        </div>
      )}

      {error && !isLoading && (
        <div className="max-w-lg mx-auto bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-4" />
          <p className="text-sm text-zinc-400">{error}</p>
        </div>
      )}

      {hasData && (
        <div className="space-y-6 animate-fade-in">
          {/* Tabs */}
          <div className="flex items-center gap-1.5">
            <Filter className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
            <button
              onClick={() => { setActiveTab('underpaid'); setSortField('difference'); setSortDir('asc'); }}
              className={`px-3 py-1.5 text-xs rounded-md whitespace-nowrap transition-colors ${
                activeTab === 'underpaid'
                  ? 'bg-red-500/10 text-red-400 border border-red-500/30'
                  : 'text-zinc-500 hover:text-zinc-300 border border-transparent'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <TrendingDown className="h-3 w-3" />
                Underpaid ({underpaidIssues.length})
              </span>
            </button>
            <button
              onClick={() => { setActiveTab('unmatched'); setSortField('invoiced'); setSortDir('desc'); }}
              className={`px-3 py-1.5 text-xs rounded-md whitespace-nowrap transition-colors ${
                activeTab === 'unmatched'
                  ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30'
                  : 'text-zinc-500 hover:text-zinc-300 border border-transparent'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <Unlink className="h-3 w-3" />
                Unmatched ({unmatchedIssues.length})
              </span>
            </button>
          </div>

          {/* Summary Cards - Underpaid */}
          {activeTab === 'underpaid' && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="h-4 w-4 text-red-400" />
                  <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Total Underpaid</span>
                </div>
                <p className="text-lg font-bold text-red-400 tabular-nums">{fmtCurrency(totalUnderpaid)}</p>
                <p className="text-[10px] text-zinc-500 mt-1">{underpaidIssues.length} accounts</p>
              </div>
              <div className="rounded-xl border border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)] p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Download className="h-4 w-4 text-zinc-400" />
                  <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Invoiced (Sheets)</span>
                </div>
                <p className="text-lg font-bold text-zinc-300 tabular-nums">{fmtCurrency(totalInvoiced)}</p>
                <p className="text-[10px] text-zinc-600 mt-1">From Raketech invoice</p>
              </div>
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowDownRight className="h-4 w-4 text-amber-400" />
                  <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Should Be</span>
                </div>
                <p className="text-lg font-bold text-amber-400 tabular-nums">{fmtCurrency(totalShouldBe)}</p>
                <p className="text-[10px] text-zinc-600 mt-1">Max of Recon / Synced</p>
              </div>
              <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Scale className="h-4 w-4 text-emerald-400" />
                  <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Our Synced (AC)</span>
                </div>
                <p className="text-lg font-bold text-emerald-400 tabular-nums">{fmtCurrency(totalSynced)}</p>
                <p className="text-[10px] text-zinc-600 mt-1">{syncedAccountCount} with data</p>
              </div>
              <div className="rounded-xl border border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)] p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-red-400/70" />
                  <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Synced vs Recon</span>
                </div>
                <p className={`text-lg font-bold tabular-nums ${totalSynced - totalShouldBe < -1 ? 'text-red-400' : 'text-zinc-300'}`}>
                  {fmtCurrency(totalSynced - totalShouldBe)}
                </p>
                <p className="text-[10px] text-zinc-600 mt-1">Our data vs should be</p>
              </div>
            </div>
          )}

          {/* Summary Cards - Unmatched */}
          {activeTab === 'unmatched' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Unlink className="h-4 w-4 text-amber-400" />
                  <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Unmatched Accounts</span>
                </div>
                <p className="text-xl font-bold text-amber-400 tabular-nums">{unmatchedIssues.length}</p>
              </div>
              <div className="rounded-xl border border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)] p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Download className="h-4 w-4 text-zinc-400" />
                  <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Invoiced (Unverified)</span>
                </div>
                <p className="text-xl font-bold text-zinc-300 tabular-nums">{fmtCurrency(unmatchedInvoiceTotal)}</p>
              </div>
              <div className="rounded-xl border border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)] p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowDownRight className="h-4 w-4 text-purple-400" />
                  <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Recon Earnings</span>
                </div>
                <p className="text-xl font-bold text-purple-400 tabular-nums">{fmtCurrency(unmatchedReconTotal)}</p>
              </div>
            </div>
          )}

          {/* Search */}
          <div className="relative max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search accounts..."
              className="pl-8 pr-3 py-1.5 w-full text-xs bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-red-500/50"
            />
          </div>

          {/* Table */}
          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[hsl(225,15%,8%)]">
                  {activeTab === 'underpaid' ? (
                    <tr>
                      <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">Severity</th>
                      <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">Account ID</th>
                      <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">Username</th>
                      <SortHeader field="partner" label="Partner" />
                      <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">GEO</th>
                      <th className="text-right px-3 py-2.5 text-zinc-500 font-medium">Invoiced</th>
                      <th className="text-right px-3 py-2.5 text-zinc-500 font-medium">Reconciliation</th>
                      <th className="text-right px-3 py-2.5 text-zinc-500 font-medium">Our Synced</th>
                      <th className="text-right px-3 py-2.5 text-zinc-500 font-medium">Manual Check</th>
                      <SortHeader field="difference" label="Diff" align="text-right" />
                      <SortHeader field="percentage" label="%" align="text-right" />
                      <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">Account</th>
                    </tr>
                  ) : (
                    <tr>
                      <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">Raketech ID</th>
                      <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">Username</th>
                      <SortHeader field="partner" label="Partner" />
                      <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">GEO</th>
                      <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">Offer</th>
                      <SortHeader field="invoiced" label="Invoiced" align="text-right" />
                      <th className="text-right px-3 py-2.5 text-zinc-500 font-medium">Recon (EUR)</th>
                      <th className="text-right px-3 py-2.5 text-zinc-500 font-medium">Partner 95%</th>
                      <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">Details</th>
                    </tr>
                  )}
                </thead>
                <tbody className="divide-y divide-[hsl(225,15%,13%)]">
                  {activeTab === 'underpaid' && filtered.map((iss, idx) => {
                    const diff = iss.revenueDifference || 0;
                    const pct = iss.percentageDifference || 0;
                    const severityCls: Record<string, string> = {
                      critical: 'bg-red-500/10 text-red-400 border-red-500/20',
                      high: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
                      medium: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
                    };
                    return (
                      <tr key={`${iss.accountId}-${idx}`} className="hover:bg-[hsl(225,15%,12%)]">
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium ${severityCls[iss.severity] || 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'}`}>
                            {iss.severity}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-zinc-300 tabular-nums">{iss.accountId}</td>
                        <td className="px-3 py-2.5 text-white font-medium">{iss.username}</td>
                        <td className="px-3 py-2.5 text-zinc-400">{iss.partner}</td>
                        <td className="px-3 py-2.5">
                          <span className="text-purple-400 font-medium">{iss.geo}</span>
                        </td>
                        <td className="px-3 py-2.5 text-right text-zinc-300 tabular-nums">
                          {iss.invoiceRevenue !== undefined ? fmtCurrency(iss.invoiceRevenue) : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-right text-amber-400 tabular-nums">
                          {iss.reconciliationEarnings !== undefined ? fmtCurrency(iss.reconciliationEarnings) : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          {iss.adwstatsAccountId ? (
                            iss.adwstatsDataSynced === false ? (
                              <span className="text-amber-400/70 text-[10px]">not synced</span>
                            ) : (
                              <span className="text-emerald-400">
                                {fmtCurrency(iss.adwstatsCommission || 0)}
                                {iss.adwstatsCurrency && iss.adwstatsCurrency !== 'EUR' && iss.adwstatsCommissionOriginal != null && (
                                  <span className="block text-[9px] text-emerald-400/50">
                                    {fmtOriginalCurrency(iss.adwstatsCommissionOriginal, iss.adwstatsCurrency)}
                                  </span>
                                )}
                              </span>
                            )
                          ) : (
                            <span className="text-zinc-600">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          {iss.manualCheckAmount ? (
                            <span className="text-cyan-400" title={iss.manualCheckComment || ''}>
                              {fmtCurrency(iss.manualCheckAmount)}
                              {iss.manualCheckSource && (
                                <span className="block text-[9px] text-cyan-400/60">{iss.manualCheckSource}</span>
                              )}
                            </span>
                          ) : <span className="text-zinc-600">—</span>}
                        </td>
                        <td className="px-3 py-2.5 text-right text-red-400 tabular-nums font-medium">
                          {fmtCurrency(diff)}
                        </td>
                        <td className="px-3 py-2.5 text-right text-red-400/70 tabular-nums">
                          {pct.toFixed(1)}%
                        </td>
                        <td className="px-3 py-2.5">
                          {iss.adwstatsAccountName ? (
                            <span className="text-zinc-400 text-[10px]">{iss.adwstatsAccountName}</span>
                          ) : (
                            <span className="text-zinc-600 text-[10px]">unmatched</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {activeTab === 'unmatched' && filtered.map((iss, idx) => (
                    <tr key={`${iss.accountId}-${idx}`} className="hover:bg-[hsl(225,15%,12%)]">
                      <td className="px-3 py-2.5 text-zinc-300 tabular-nums font-medium">{iss.accountId}</td>
                      <td className="px-3 py-2.5 text-white font-medium">{iss.username}</td>
                      <td className="px-3 py-2.5 text-zinc-400">{iss.partner}</td>
                      <td className="px-3 py-2.5">
                        <span className="text-purple-400 font-medium">{iss.geo}</span>
                      </td>
                      <td className="px-3 py-2.5 text-zinc-500 max-w-[140px] truncate">{iss.offer || '—'}</td>
                      <td className="px-3 py-2.5 text-right text-zinc-300 tabular-nums">
                        {iss.invoiceRevenue !== undefined ? fmtCurrency(iss.invoiceRevenue) : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right text-amber-400 tabular-nums">
                        {iss.reconciliationEarnings !== undefined ? fmtCurrency(iss.reconciliationEarnings) : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right text-blue-400 tabular-nums">
                        {iss.partnerCommissionOurShare !== undefined ? fmtCurrency(iss.partnerCommissionOurShare) : '—'}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="text-amber-400/70 text-[10px] flex items-center gap-1">
                          <Unlink className="h-3 w-3" />
                          No adwstats match
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filtered.length === 0 && (
              <div className="p-8 text-center text-zinc-500 text-sm">No results match your search</div>
            )}
          </div>
        </div>
      )}

      {!isLoading && !error && !hasData && (
        <div className="max-w-lg mx-auto bg-[hsl(225,15%,10%)] rounded-xl border border-emerald-500/20 p-8 text-center">
          <Scale className="h-10 w-10 text-emerald-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">No Commission Errors</h2>
          <p className="text-sm text-zinc-400">All accounts are correctly paid. No underpayments detected.</p>
        </div>
      )}
    </div>
  );
};

export default RaketechUnderpaid;
